import axios, {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    AxiosResponseTransformer,
    AxiosError,
} from 'axios';
import axiosRetry from 'axios-retry';

import { isJsonString } from 'src/common/helpers';
import CookiesHelper from 'src/common/helpers/CookieHelpers';
import { clearLocalStorageExcept } from 'src/common/helpers/localstorage-utils';
import { BASE_URL, NODE_ENV } from 'src/config';

export default class HttpClient {
    private static transformResponse(
        input: string,
    ): AxiosResponseTransformer | AxiosResponseTransformer[] {
        return JSON.parse(input);
    }

    private static client(header = {}): AxiosInstance {
        // cancelToken and source declaration
        const cancelTokenSource = axios.CancelToken.source();

        // axios client config
        const config: AxiosRequestConfig = {
            baseURL: BASE_URL,
            cancelToken: cancelTokenSource.token,
            headers: {
                Accept: 'application/json',
                platform: 'web-app',
                'Content-Type': 'application/x-www-form-urlencoded',
                ...header,
            },
        };

        // axios client response transformer
        config.transformResponse = [
            (data) => {
                // TODO:: Remove if condition and return only else part once configV2js API is fixed from backend
                if (!isJsonString(data)) {
                    return data;
                } else {
                    return data && typeof data === 'string' ? this.transformResponse(data) : data;
                }
            },
        ];

        // create axios client
        const Axios = axios.create(config);

        axiosRetry(Axios, {
            retries: 3,
            retryDelay: (retryCount) => retryCount * 1000,
            retryCondition: (error) => {
                const retryStatusCodes = [500, 502, 503, 504, 408, 429];
                return error.response && retryStatusCodes.includes(error.response.status);
            },
        });

        Axios.interceptors.request.use(
            async (config: any) => {
                const c_token = CookiesHelper.read('c_token');
                if (c_token && config.url && !config.url.includes('c_token=')) {
                    const separator = config.url.includes('?') ? '&' : '?';
                    config.url += `${separator}c_token=${c_token}`;
                }
                config.headers['Accept-Language'] = window.localStorage.lang;

                return config;
            },
            (err: any) => Promise.reject(err),
        );
        Axios.interceptors.response.use(
            (response: AxiosResponse) => response,
            async (error: AxiosError) => {
                const response = JSON.parse(error.request.response);
                if (response.code === 30000) {
                    await HttpClient.post('/api/user/authorize_cancel', {});
                }

                if (response.code === 30000 || response.status === 403) {
                    CookiesHelper.clearAll();
                    clearLocalStorageExcept(['isSimplifiedTradeActive']);
                    if (NODE_ENV === 'development') {
                        alert(
                            'Login session expired! \n\nENABLE_LOGIN_STATE is true, Update the cookies keys or set ENABLE_LOGIN_STATE as false',
                        );
                    }
                    window.location.reload();
                    // TODO:: Needs to display user a message in case of logout by system
                }
                return Promise.reject(error);
            },
        );
        return Axios;
    }

    /**
     *
     * @param url
     * @returns
     */
    public static get(
        url: string,
        config?: AxiosRequestConfig<any> | undefined,
    ): Promise<AxiosResponse> {
        return this.client().get(url, config);
    }

    /**
     *
     * @param url
     * @param payload
     * @returns
     */
    public static post<T>(url: string, payload: T, headers = {}): Promise<AxiosResponse> {
        return this.client().post(url, payload, { headers: headers });
    }
    /**
     *
     * @param url
     * @param payload
     * @returns
     */
    public static patch<T>(url: string, payload: T, headers = {}): Promise<AxiosResponse> {
        return this.client().patch(url, payload, { headers: headers });
    }
    /**
     *
     * @param url
     * @param payload
     * @returns
     */
    public static put<T>(url: string, payload: T, headers = {}): Promise<AxiosResponse> {
        return this.client().put(url, payload, { headers: headers });
    }
    /**
     *
     * @param url
     * @returns
     */
    public static delete(url: string, headers = {}): Promise<AxiosResponse> {
        return this.client().delete(url, { headers: headers });
    }
}
